import { Fragment, useMemo, useState, useCallback } from 'react';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarActions,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { useLayout } from '@/providers';
import { DataGrid, KeenIcon } from '@/components';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  useGetHelpdeskFAQsQuery,
  useDeleteHelpdeskFAQMutation,
} from '@/services/HelpdeskFAQService';
import { HelpdeskFAQ } from '@/types/cms.types';
import { ConfirmationDialog } from '@/components/dialogBox';
import { usePermissions } from '@/hooks/usePermissions';
import { PaginationState } from '@tanstack/react-table';

const HelpdeskFAQListing = () => {
  const { currentLayout } = useLayout();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { hasPermission } = usePermissions();

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    faqId: string | null;
  }>({
    isOpen: false,
    faqId: null,
  });

  const {
    data: faqsData,
    isLoading,
    refetch,
  } = useGetHelpdeskFAQsQuery({
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: searchQuery || undefined,
  });

  const [deleteFAQ] = useDeleteHelpdeskFAQMutation();

  const handleDelete = async () => {
    if (!deleteModal.faqId) return;

    try {
      await deleteFAQ(deleteModal.faqId).unwrap();
      enqueueSnackbar('FAQ deleted successfully', { variant: 'success' });
      setDeleteModal({ isOpen: false, faqId: null });
      refetch();
    } catch {
      enqueueSnackbar('Failed to delete FAQ', { variant: 'error' });
    }
  };

  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const truncateText = (text: string, maxLength: number) => {
    const strippedText = text.replace(/<[^>]*>/g, '');
    if (strippedText.length <= maxLength) return strippedText;
    return strippedText.substring(0, maxLength) + '...';
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: 'Title',
        cell: ({ row }: { row: { original: HelpdeskFAQ } }) => (
          <span
            className="font-medium text-gray-800"
            title={row.original.title}
          >
            {truncateText(row.original.title, 50)}
          </span>
        ),
      },
      {
        accessorKey: 'body',
        header: 'Body Preview',
        cell: ({ row }: { row: { original: HelpdeskFAQ } }) => (
          <span className="text-gray-600" title={row.original.body}>
            {truncateText(row.original.body, 100)}
          </span>
        ),
      },
      {
        accessorKey: 'is_helpful_count',
        header: 'Helpful',
        cell: ({ row }: { row: { original: HelpdeskFAQ } }) => (
          <span className="badge badge-outline badge-success contrast:text-green-100 gap-2">
            <KeenIcon icon="like" className="text-xs" />
            {row.original.is_helpful_count}
          </span>
        ),
        meta: { className: 'w-24' },
      },
      {
        accessorKey: 'is_not_helpful_count',
        header: 'Not Helpful',
        cell: ({ row }: { row: { original: HelpdeskFAQ } }) => (
          <span className="badge badge-outline gap-2">
            <KeenIcon icon="dislike" className="text-xs" />{' '}
            {row.original.is_not_helpful_count}
          </span>
        ),
        meta: { className: 'w-24' },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }: { row: { original: HelpdeskFAQ } }) => (
          <div className="flex gap-2">
            <button
              className="btn btn-sm btn-icon btn-light"
              onClick={() => navigate(`/helpdesk-faq/view/${row.original.id}`)}
              title="View"
            >
              <KeenIcon icon="eye" />
            </button>
            {hasPermission('faq_management_update') && (
              <button
                className="btn btn-sm btn-icon btn-light"
                onClick={() =>
                  navigate(`/helpdesk-faq/edit/${row.original.id}`)
                }
                title="Edit"
              >
                <KeenIcon icon="pencil" />
              </button>
            )}
            {hasPermission('faq_management_delete') && (
              <button
                className="btn btn-sm btn-icon btn-light"
                onClick={() =>
                  setDeleteModal({
                    isOpen: true,
                    faqId: row.original.id,
                  })
                }
                title="Delete"
              >
                <KeenIcon icon="trash" className="text-danger" />
              </button>
            )}
          </div>
        ),
        meta: { className: 'w-32' },
      },
    ],
    [navigate, hasPermission]
  );

  return (
    <Fragment>
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle id="helpdesk-faq-heading" text="Helpdesk FAQ" />
              <ToolbarDescription>
                Manage helpdesk FAQ entries
              </ToolbarDescription>
            </ToolbarHeading>
            <ToolbarActions>
              {hasPermission('faq_management_create') && (
                <button
                  className="btn btn-sm btn-light"
                  onClick={() => navigate('/helpdesk-faq/create')}
                >
                  Add New FAQ
                </button>
              )}
            </ToolbarActions>
          </Toolbar>
        </Container>
      )}

      <div className="container-fixed">
        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-2">
              <div className="relative">
                <KeenIcon
                  icon="magnifier"
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search by title..."
                  aria-label="Search by title"
                  className="input input-sm pl-10 w-64 h-10"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>
            </div>
          </div>
          <div className="card-body">
            <DataGrid
              columns={columns}
              data={faqsData?.data || []}
              loading={isLoading}
              serverSide={true}
              pagination={{
                page: pagination.pageIndex,
                size: pagination.pageSize,
                totalItems: faqsData?.total || 0,
                totalPages: faqsData?.totalPages || 0,
                setPagination,
              }}
            />
          </div>
        </div>
      </div>

      <ConfirmationDialog
        open={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, faqId: null })}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this FAQ? This action cannot be undone."
      />
    </Fragment>
  );
};

export { HelpdeskFAQListing };
